// Pending Checkout Utility - Handles checkout state across OAuth redirects
// with idempotency protection to prevent double checkout

export interface PendingCheckout {
    intentId: string;      // UUID for idempotency
    tripId: string;
    tripSlug: string;
    priceId: string;
    plan: 'trip_unlock';   // Only trip unlock in promo flow
    returnTo: string;
    createdAt: number;
}

const PENDING_CHECKOUT_KEY = 'raisa_pending_checkout';
const CHECKOUT_INFLIGHT_KEY = 'raisa_checkout_inflight';
const TTL_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Save pending checkout before OAuth redirect
 */
export function savePendingCheckout(
    data: Omit<PendingCheckout, 'createdAt' | 'intentId'>
): string {
    const intentId = crypto.randomUUID();
    localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({
        ...data,
        intentId,
        createdAt: Date.now()
    }));
    return intentId;
}

/**
 * Get pending checkout if valid (not expired)
 */
export function getPendingCheckout(): PendingCheckout | null {
    try {
        const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
        if (!raw) return null;

        const data = JSON.parse(raw) as PendingCheckout;

        // Check TTL
        if (Date.now() - data.createdAt > TTL_MS) {
            clearPendingCheckout();
            return null;
        }

        return data;
    } catch {
        clearPendingCheckout();
        return null;
    }
}

/**
 * Clear pending checkout
 */
export function clearPendingCheckout(): void {
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
}

/**
 * Check if checkout has expired
 */
export function isPendingCheckoutExpired(): boolean {
    try {
        const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
        if (!raw) return false;

        const data = JSON.parse(raw) as PendingCheckout;
        return Date.now() - data.createdAt > TTL_MS;
    } catch {
        return false;
    }
}

// =============================================================================
// IDEMPOTENCY - Prevent double checkout
// =============================================================================

/**
 * Check if a checkout is currently in flight
 */
export function isCheckoutInFlight(): boolean {
    return localStorage.getItem(CHECKOUT_INFLIGHT_KEY) !== null;
}

/**
 * Mark checkout as in flight
 */
export function setCheckoutInFlight(intentId: string): void {
    localStorage.setItem(CHECKOUT_INFLIGHT_KEY, intentId);
}

/**
 * Clear checkout in flight flag
 */
export function clearCheckoutInFlight(): void {
    localStorage.removeItem(CHECKOUT_INFLIGHT_KEY);
}
